import React from "react";
import { useDispatch } from "react-redux";

import { ForwardIcon, MasterCard, Visa } from "../../assets/DirectTopUpIcon";
import {
  purchaseAirtime,
  resetPurchaseState as resetAirtimeState,
  selectPurchaseLoading as selectAirtimeLoading,
  selectPurchasePaymentUrl as selectAirtimePaymentUrl,
  selectPurchaseError as selectAirtimeError,
} from "../../features/store/airtimePurchaseSlice";
import {
  purchaseData,
  resetPurchaseState as resetDataState,
  selectPurchaseLoading as selectDataLoading,
  selectPurchasePaymentUrl as selectDataPaymentUrl,
  selectPurchaseError as selectDataError,
} from "../../features/store/dataPurchaseSlice";

import {
  purchaseElectricity,
  resetPurchaseState as resetElectricState,
  selectPurchaseLoading as selectElectricLoading,
  selectPurchasePaymentUrl as selectElectricPaymentUrl,
  selectPurchaseError as selectElectricError,
} from "../../features/store/electricityPurchaseSlice";
import { AppDispatch } from "../../redux/store";
import { useAppSelector } from "../../hooks/hooks";
import ResponseModal from "../ResponseModal";
import { TransactionData } from "../types";
import { AirtimePurchaseRequest } from "../../features/store/types/airtime-types";
import { DataPurchaseRequest } from "../../features/store/types/data-purchase-types";
import { ElectricityPurchaseRequest } from "../../features/store/types/electricity-types";
import useClickOutside from "../../hooks/useClickOutside";
import { formatCurrency } from "../../utils/utils";
import BankTransferDetails from "./BankTransferDetails";
import {
  resetPurchaseState as resetTransferState,
  setPaymentDetails as setTransferPaymentDetails,
  selectTransferPayment,
} from "../../features/store/transferPaymentSlice";

const PaymentModal = (props: TransactionData) => {
  const dispatch = useDispatch<AppDispatch>();
  const airtimeLoading = useAppSelector(selectAirtimeLoading);
  const airtimePaymentUrl = useAppSelector(selectAirtimePaymentUrl);
  const airtimeError = useAppSelector(selectAirtimeError);

  const dataLoading = useAppSelector(selectDataLoading);
  const dataPaymentUrl = useAppSelector(selectDataPaymentUrl);
  const dataError = useAppSelector(selectDataError);

  const electricityLoading = useAppSelector(selectElectricLoading);
  const electricityPaymentUrl = useAppSelector(selectElectricPaymentUrl);
  const electricityError = useAppSelector(selectElectricError);
  const transferPayment = useAppSelector(selectTransferPayment);

  const clickref = useClickOutside(props.togglePaymentModal);

  const { user } = useAppSelector((state) => state.auth);

  const loading =
    props.type === "airtime"
      ? airtimeLoading
      : props.type == "data"
      ? dataLoading
      : electricityLoading;
  const paymentUrl =
    props.type === "airtime"
      ? airtimePaymentUrl
      : props.type == "data"
      ? dataPaymentUrl
      : electricityPaymentUrl;
  const error =
    props.type === "airtime"
      ? airtimeError
      : props.type == "data"
      ? dataError
      : electricityError;

  const [responseModal, setResponseModal] = React.useState(false);
  const [responseMessage, setResponseMessage] = React.useState("");
  const [responseStatus, setResponseStatus] = React.useState<
    "success" | "error"
  >("success");
  // track which payment method is currently processing locally
  const [processingMethod, setProcessingMethod] = React.useState<
    "ATM" | "TRANSFER" | "COIN" | null
  >(null);

  const closeResponseModal = () => {
    setResponseModal(false);
  };

  // Redirect to payment URL if available
  React.useEffect(() => {
    if (paymentUrl) {
      window.location.href = paymentUrl;
    }
  }, [paymentUrl]);

  // clear local processing flag when global loading stops
  React.useEffect(() => {
    if (!loading) {
      setProcessingMethod(null);
    }
  }, [loading]);

  React.useEffect(() => {
    if (!props.isOpen) {
      dispatch(resetTransferState());
    }
  }, [props.isOpen, dispatch]);

  if (!props.isOpen) return null;

  function getOriginalPrice(discountedPrice: number, discountPercent: number) {
    const originalPrice = discountedPrice / (1 - discountPercent / 100);
    return originalPrice;
  }

  const getTransferAmount = () => {
    if (props.type === "airtime" || props.type === "electric") {
      const asNumber = Number(props.additionalInputValue);
      return Number.isFinite(asNumber)
        ? asNumber
        : Number(props.selectedPlan.wallet_price);
    }
    return Number(props.selectedPlan.wallet_price);
  };

  // Generic payment handler for ATM, Bank Transfer and Coin (points)
  const handlePayment = async (method: "ATM" | "TRANSFER" | "COIN") => {
    // reset any previous purchase state and mark this method as processing
    switch (props.type) {
      case "airtime":
        dispatch(resetAirtimeState());
        break;
      case "data":
        dispatch(resetDataState());
        break;
      case "electric":
        dispatch(resetElectricState());
        break;
      default:
        // unknown product
        setResponseStatus("error");
        setResponseMessage("Unsupported product type.");
        setResponseModal(true);
        return;
    }
    setProcessingMethod(method);
    if (method !== "TRANSFER") {
      setResponseModal(true);
      setResponseStatus("success"); // Default to success, will be updated
      setResponseMessage("Processing your payment...");
    } else {
      setResponseModal(false);
      dispatch(resetTransferState());
    }

    let payload:
      | AirtimePurchaseRequest
      | DataPurchaseRequest
      | ElectricityPurchaseRequest;
    switch (props.type) {
      case "airtime":
        payload = {
          unique_number: props.unique_number || "",
          plan_id: props.selectedPlan.id,
          payment_method: method,
          amount: Number(props.additionalInputValue),
          source: "WEB",
        } as AirtimePurchaseRequest;
        break;
      case "data":
        payload = {
          unique_number: props.unique_number || "",
          plan_id: props.selectedPlan.id,
          payment_method: method,
          source: "WEB",
        } as DataPurchaseRequest;
        break;
      case "electric":
        payload = {
          unique_number: props.unique_number || "",
          plan_id: props.selectedPlan.id,
          payment_method: method,
          amount: Number(props.additionalInputValue),
          source: "WEB",
        } as ElectricityPurchaseRequest;
        break;
      default:
        setResponseStatus("error");
        setResponseMessage("Unsupported product type.");
        return;
    }

    try {
      let action;
      switch (props.type) {
        case "airtime":
          action = purchaseAirtime(payload);
          break;
        case "data":
          action = purchaseData(payload);
          break;
        case "electric":
          action = purchaseElectricity(payload);
          break;
        default:
          setResponseStatus("error");
          setResponseMessage("Unsupported product type.");
          return;
      }

      const res: any = await dispatch(action).unwrap();

      if (method === "TRANSFER" && res?.data?.account_number) {
        dispatch(
          setTransferPaymentDetails({
            bank: res.data.bank_name,
            bank_name: res.data.bank_name,
            account_name: res.data.account_name,
            account_number: res.data.account_number,
            reference: res.data.reference,
            accountExpiry: res.data.accountExpiry,
            amount: getTransferAmount(),
          })
        );
        setProcessingMethod(null);
        setResponseModal(false);
        return;
      }

      setResponseStatus("success");
      setResponseMessage(res?.message || "Payment successful!");
    } catch (err: any) {
      // unwrap throws the rejected action payload or error
      const msg =
        err?.message || err?.payload?.message || err?.error || "Payment failed";
      setResponseStatus("error");
      setResponseMessage(msg);
      setResponseModal(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
      <div
        ref={clickref}
        className="bg-white dark:bg-black rounded-lg p-6 w-[290px] bp:w-full max-w-md mx-[10px] h-[550px] bp:h-[684px] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xs bp:text-base font-semibold text-black dark:text-white">
            Checkout
          </h2>
          <button
            onClick={props.togglePaymentModal}
            className="text-[#475467] dark:text-white bg-[#F4F4F5] dark:bg-[#FFFFFF1A] rounded-2xl h-6 w-5 hover:text-gray-700  dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        <div className="bg-[#FFF9E7] h-10 dark:bg-[#DC6803] p-3 rounded-3xl mb-6 flex items-center justify-center">
          <img
            src="/PNGS/Coins.png"
            alt="Coins"
            className="w-[24px] h-[24px]"
          />
          <p className="text-[10px] bp:text-sm text-[#DC6803] dark:text-white">
            You can earn coins from this purchase
          </p>
        </div>

        <div className="flex items-center gap-3 mb-2 bp:mb-6">
          <div>
            <img
              src={props.selectedPlan.image_url}
              alt="product_img"
              className="w-[102px] h-[75px] bp:h-[102px]"
            />
          </div>
          <div className="border border-border-border1 dark:border-tabs p-2 rounded-lg w-[368px] h-[75px] bp:h-[102px]">
            <h3 className="font-bold text-xs bp:text-base font-lt text-black dark:text-white">
              {props.type} purchase
            </h3>
            <p className="font-lt text-[10px] bp:text-sm  text-gray-500 dark:text-gray-400">
              {props.selectedPlan?.category?.provider?.name} :{" "}
              {props.selectedPlan?.name} ( {props.selectedPlan?.category?.name})
            </p>
            <div className="flex items-center mt-1 gap-2">
              <div className="bg-[#039EF2] text-white px-2 rounded-2xl flex items-center gap-1">
                <img
                  src="/PNGS/Coins.png"
                  alt="Coins"
                  className="w-[20px] h-[20px]"
                />
                <span className="text-[10px] bp:text-xs font-semibold">
                  {formatCurrency(props.selectedPlan.wallet_price)}
                </span>
              </div>
              <span className="text-xs bp:text-base text-black dark:text-white">
                ₦{formatCurrency(props.selectedPlan.wallet_price)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 py-4">
          <div className="flex justify-between mb-1 bp:mb-2 font-inter">
            <span className="text-[#667085] dark:text-white text-[10px] bp:text-sm">
              Subtotal
            </span>
            <span className="text-black text-[10px] bp:text-sm dark:text-white ">
              {props.selectedPlan.discount > 0
                ? formatCurrency(
                    getOriginalPrice(
                      props.selectedPlan.wallet_price,
                      props.selectedPlan.discount
                    )
                  )
                : formatCurrency(props.selectedPlan.wallet_price)}
            </span>
          </div>
          {props.selectedPlan.discount > 0 && (
            <div className="flex justify-between mb-1 bp:mb-2 font-inter">
              <span className="text-[#667085] text-[10px] bp:text-sm dark:text-white">
                Discount
              </span>
              <span className="text-black dark:text-white text-[10px] bp:text-sm">
                {formatCurrency(
                  getOriginalPrice(
                    props.selectedPlan.wallet_price,
                    props.selectedPlan.discount
                  ) - props.selectedPlan.wallet_price
                )}
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-dashed border-gray-200 dark:border-white py-4 font-inter">
          <div className="flex justify-between items-center">
            <span className="text-[#101828] text-[10px] bp:text-sm dark:text-gray-400">
              Total
            </span>
            <div className="flex items-center">
              <div className="bg-[#039EF2] text-white  px-2 py-1 rounded-2xl gap-1 flex items-center mr-2">
                <div className="w-4 h-4  flex items-center">
                  <img
                    src="/PNGS/Coins.png"
                    alt="Coins"
                    className="w-[20px] h-[20px]"
                  />
                </div>
                <span className=" text-[10px] bp:text-xs font-inter font-semibold">
                  {formatCurrency(props.selectedPlan.wallet_price)}
                </span>
              </div>
              <span className="text-[10px] bp:text-sm font-bold text-black dark:text-white">
                ₦{formatCurrency(props.selectedPlan.wallet_price)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-2 bp:mt-6 bg-[#EAECF0] dark:bg-[#FFFFFF1A] rounded-lg p-4">
          {transferPayment.paymentDetails ? (
            <BankTransferDetails />
          ) : (
            <>
              {/* Coin payment button (now clickable) */}
              {user && (
                <>
                  <button
                    className={`relative h-[50px] bp:h-[60px] w-full max-w-md flex items-center justify-between rounded-lg p-4 overflow-hidden ${
                      loading && processingMethod !== "COIN"
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                    style={{
                      backgroundImage: `url(/images/LeaderBoardBG.jpeg)`,
                      backgroundPosition: "center bottom",
                      backgroundSize: "cover",
                    }}
                    onClick={() => handlePayment("COIN")}
                    disabled={loading && processingMethod !== "COIN"}
                  >
                    {/* Left side with coin icon */}
                    <div className="flex items-center gap-3 z-10">
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 mr-1 flex items-center gap-1">
                          <img
                            src="/PNGS/Coins.png"
                            alt="Coins"
                            className="w-[40px] h-[40px]"
                          />
                          <div className="flex flex-col mt-2">
                            <p className="text-[8px] font-medium text-blue-100">
                              Pay with available coins
                            </p>
                            <p className="text-xl font-lt font-bold text-white">
                              {processingMethod === "COIN" && loading
                                ? "Processing..."
                                : user.dg_coin_wallet_balance}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right side with arrow icon */}
                    <div className="bg-white  w-5 h-5 bp:w-6 bp:h-6 flex items-center rounded-full p-2 z-10">
                      <ForwardIcon />
                    </div>
                  </button>
                  <div className="flex items-center my-4">
                    <div className="flex-grow h-px bg-gray-200"></div>
                    <span className="px-3 text-gray-500 text-sm uppercase">
                      OR
                    </span>
                    <div className="flex-grow h-px bg-gray-200"></div>
                  </div>
                </>
              )}

              {/* Bank Transfer Payment Button */}

              <button
                className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                  loading && processingMethod !== "TRANSFER"
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => handlePayment("TRANSFER")}
                disabled={loading && processingMethod !== "TRANSFER"}
              >
                <div className="flex space-x-2 mr-2"></div>
                <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                  {processingMethod === "TRANSFER" && loading
                    ? "Processing..."
                    : "Pay with Bank Transfer"}
                </span>
              </button>

              <div className="flex items-center my-4">
                <div className="flex-grow h-px bg-gray-200"></div>
                <span className="px-3 text-gray-500 text-sm uppercase">OR</span>
                <div className="flex-grow h-px bg-gray-200"></div>
              </div>

              {/* ATM Payment Button */}
              <button
                className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                  loading && processingMethod !== "ATM"
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => handlePayment("ATM")}
                disabled={loading && processingMethod !== "ATM"}
              >
                <div className="flex space-x-2 mr-2">
                  <span className="w-8">
                    <Visa />
                  </span>
                  <span className="w-8">
                    <MasterCard />
                  </span>
                </div>
                <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                  {processingMethod === "ATM" && loading
                    ? "Processing..."
                    : "Pay with your ATM"}
                </span>
              </button>

              {error && (
                <div className="text-red-500 text-xs mt-2 text-center">
                  {error}
                </div>
              )}
            </>
          )}
          <ResponseModal
            isOpen={responseModal}
            onClose={closeResponseModal}
            status={responseStatus}
            message={responseMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
